import { useCallback } from 'react';
import _, { isEmpty } from 'lodash';

import { makeStyles } from '@material-ui/core/styles';
import { Box, CircularProgress, LinearProgress } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { useQueryLoader } from '../../data-table/Loaders';
import { useNavigate } from '../../../utilities/PgmUtilities';
import { AdminPages, buildPageUrl } from '../AdminLinkUtils';
import {
    EDIT_SCOPE_SUBNET_QUERY_PARAM,
    getIpScopeListColumns,
    ipScopeListDataSource,
} from './CollectorsAdminConstants';

const initialSort = [{ property: 'name', direction: SORT_ASC }];
const dataSource = ipScopeListDataSource();

const useStyles = makeStyles((theme) => ({
    tabTable: {
        paddingTop: theme.spacing(1),

        '@media print': {
            paddingTop: 0,
        },
    },
    indexContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentFlexStart,
        ...theme.mixins.flex.alignItemsCenter,
        minWidth: 210,
    },
}));

const ScanStatusComponent = ({ ipScopeScan }) => {
    const classes = useStyles();

    if (_.isNil(ipScopeScan)) return;

    return (
        <>
            {ipScopeScan.isScanning === false ? (
                <div className={classes.indexContainer}>
                    <Box mr={1} height={18}>
                        <CircularProgress size={14} />
                    </Box>
                    <div>
                        <Trans>Preparing scan (initiated by {ipScopeScan.userName})</Trans>
                    </div>
                </div>
            ) : (
                <div>
                    <Trans>
                        Scanning (initiated by {ipScopeScan.userName} {ipScopeScan.elapsedTime}). Scan is{' '}
                        {Math.round(ipScopeScan.scanProgress)}% complete, estimated time remaining:{' '}
                        {ipScopeScan.completionDate}.
                    </Trans>
                </div>
            )}
            <LinearProgress variant="determinate" value={ipScopeScan.scanProgress} />
        </>
    );
};

export default function CollectorsAdminIpScopeListPanel({
    setActionsDisabled,
    setTableSelection,
    setTableState,
    triggerRef,
    ipScopeScan,
}) {
    const classes = useStyles();
    const loader = useQueryLoader(dataSource, { disableFilterState: true });
    const navigate = useNavigate();

    const handleTableSelectionChange = useCallback(
        (currentTableSelection) => {
            const disableActions =
                !currentTableSelection ||
                (isEmpty(currentTableSelection?.selection) && !currentTableSelection.inverted);

            setTableSelection(currentTableSelection);
            setActionsDisabled(disableActions);
        },
        [setTableSelection, setActionsDisabled]
    );

    const handleEditIpScope = useCallback(
        (row) => {
            const collectorId = _.get(row, 'collector._id');
            const subnet = _.get(row, 'value.subnet');
            if (!collectorId || !subnet) return;
            const url = buildPageUrl(AdminPages.Collectors, collectorId);
            navigate(`${url}?${EDIT_SCOPE_SUBNET_QUERY_PARAM}=${encodeURIComponent(subnet)}`);
        },
        [navigate]
    );

    return (
        <>
            {ipScopeScan && <ScanStatusComponent ipScopeScan={ipScopeScan} />}
            <DataTable
                className={classes.tabTable}
                tableId="admin-collectors-list"
                loader={loader}
                columns={getIpScopeListColumns(handleEditIpScope)}
                triggerRef={triggerRef}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
                disableColumnRemover
            />
        </>
    );
}
